import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface MenuItem {
  key: string;
  label: string;
  icon: string;
  path: string;
}

const menuItems: MenuItem[] = [
  { key: 'dashboard', label: 'Dashboard', icon: 'ri-dashboard-3-line', path: '/' },
  { key: 'customers', label: 'Müşteriler', icon: 'ri-user-3-line', path: '/customers' },
  { key: 'sales', label: 'Satışlar', icon: 'ri-shopping-cart-2-line', path: '/sales' },
  { key: 'quotes', label: 'Teklifler', icon: 'ri-file-list-3-line', path: '/quotes' },
  { key: 'payments', label: 'Ödemeler', icon: 'ri-bank-card-line', path: '/payments' },
  { key: 'promissory', label: 'Senetler', icon: 'ri-file-list-3-line', path: '/promissory-notes' },
  { key: 'enforcement', label: 'İcra Takibi', icon: 'ri-scales-3-line', path: '/enforcement' },
  { key: 'payment-promises', label: 'Ödeme Sözleri', icon: 'ri-hand-heart-line', path: '/payment-promises' },
  { key: 'staff', label: 'Personel', icon: 'ri-team-line', path: '/staff' },
  { key: 'stock', label: 'Stok / Ürünler', icon: 'ri-archive-line', path: '/stock' },
  { key: 'deliveries', label: 'Teslimatlar', icon: 'ri-truck-line', path: '/deliveries' },
  { key: 'reports', label: 'Ciro & Raporlar', icon: 'ri-line-chart-line', path: '/reports' },
  { key: 'expenses', label: 'Giderler', icon: 'ri-money-dollar-circle-line', path: '/expenses' },
  { key: 'personal-loans', label: 'Elden Borçlar', icon: 'ri-hand-coin-line', path: '/personal-loans' },
  { key: 'personal-accounts', label: 'Kişisel Hesaplar', icon: 'ri-user-shared-line', path: '/personal-accounts' },
  { key: 'cash', label: 'Kasa', icon: 'ri-safe-2-line', path: '/cash' },
  { key: 'settings', label: 'Ayarlar', icon: 'ri-settings-3-line', path: '/settings' },
];

export default function Sidebar({ collapsed, mobileOpen, onToggle, onMobileClose }: {
  collapsed: boolean;
  mobileOpen?: boolean;
  onToggle: () => void;
  onMobileClose?: () => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, user, businessName, businessLogoUrl } = useAuth();

  const getActiveKey = useCallback(() => {
    const path = location.pathname;
    if (path === '/') return 'dashboard';
    const item = menuItems.find(item => item.path !== '/' && path.startsWith(item.path));
    return item ? item.key : 'dashboard';
  }, [location.pathname]);

  const [activeKey, setActiveKey] = useState(getActiveKey());

  useEffect(() => {
    setActiveKey(getActiveKey());
  }, [getActiveKey]);

  const displayName = profile?.full_name || user?.email?.split('@')[0] || 'Kullanıcı';
  const displayEmail = user?.email || '';

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden animate-fade-in"
          onClick={onMobileClose}
        />
      )}

      <aside
        className={`fixed top-0 left-0 h-full bg-background-50 border-r border-background-200 z-50 transition-all duration-300 ease-in-out flex flex-col
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
          ${collapsed ? 'lg:w-[72px]' : 'lg:w-[260px]'}
          lg:relative lg:z-auto`}
      >
        <div className={`flex items-center gap-3 px-5 h-16 border-b border-background-200 ${collapsed ? 'lg:justify-center lg:px-0' : ''}`}
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
          <div className={`flex flex-col justify-center overflow-hidden ${collapsed ? 'lg:hidden' : 'flex-1'}`}>
            {businessLogoUrl ? (
              <img
                src={businessLogoUrl}
                alt={businessName || 'İşletme logosu'}
                className="max-h-8 w-auto object-contain"
              />
            ) : (
              <div className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center flex-shrink-0">
                  <i className="ri-store-2-line text-base"></i>
                </span>
                <span className="font-semibold text-foreground-800 truncate">{businessName || 'İşletme Paneli'}</span>
              </div>
            )}
            <p className="text-[10px] text-foreground-400 mt-0.5 truncate">İşinizi Ravo ile kolaylaştırın.</p>
          </div>
          <button
            onClick={onMobileClose || onToggle}
            className="ml-auto lg:hidden w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:bg-background-100 cursor-pointer"
            style={{ minWidth: 44, minHeight: 44 }}
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-3">
          {menuItems.map((item) => (
            <button
              key={item.key}
              onClick={() => {
                navigate(item.path);
                if (onMobileClose) onMobileClose();
              }}
              className={`sidebar-link w-full text-left mb-1 ${
                activeKey === item.key ? 'sidebar-link-active' : 'sidebar-link-inactive'
              }`}
              title={item.label}
              style={{ minHeight: 44 }}
            >
              <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className={`${item.icon} text-base`}></i>
              </span>
              <span className={`${collapsed ? 'lg:hidden' : ''}`}>{item.label}</span>
            </button>
          ))}
        </nav>

        <div className={`px-3 py-3 border-t border-background-200 ${collapsed ? 'lg:px-2' : ''}`}>
          <a
            href="https://divalogin.logo.com.tr/Login.aspx"
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-3 px-4 py-2.5 rounded-lg bg-accent-500 hover:bg-accent-600 text-background-50 transition-colors cursor-pointer whitespace-nowrap ${collapsed ? 'lg:justify-center lg:px-0' : ''}`}
            title="Profilo Çağrı Aç"
          >
            <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className="ri-customer-service-2-line text-base"></i>
            </span>
            <span className={`text-sm font-medium ${collapsed ? 'lg:hidden' : ''}`}>Profilo Çağrı Aç</span>
          </a>
        </div>

        <div className={`px-3 py-3 border-t border-background-200 ${collapsed ? 'lg:px-2' : ''}`}>
          <div className={`flex items-center gap-3 px-4 py-2.5 rounded-lg ${collapsed ? 'lg:justify-center lg:px-0' : ''}`}>
            <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
              {profile?.avatar_url ? (
                <img src={profile.avatar_url} alt={displayName} className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <i className="ri-user-line text-primary-600 text-sm"></i>
              )}
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0 lg:block hidden">
                <p className="text-xs font-medium text-foreground-800 truncate">{displayName}</p>
                <p className="text-[10px] text-foreground-400 truncate">{displayEmail}</p>
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}